import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { FormState } from '../types/form.types';
import { submitCreditApplication, saveDraftApplication } from '../utils/api';

interface FormStoreState extends FormState {
  apiResponse?: any; // Réponse de l'API après soumission
}

export const useFormStore = create<FormStoreState>()(
  persist(
    (set, get) => ({
      // État initial
      formData: {
        subscription: true,
      },
      currentStepId: 'welcome',
      completedSteps: [],
      stepHistory: [],
      visitedSteps: ['welcome'],
      isSubmitting: false,
      isLoading: false,
      errors: {},
      apiResponse: undefined,

      // Actions de mise à jour des données
      updateField: (fieldName: string, value: any) => {
        set(state => ({
          formData: { 
            ...state.formData, 
            [fieldName]: value 
          },
          // Effacer l'erreur du champ mis à jour
          errors: Object.keys(state.errors).reduce((acc, key) => {
            if (key !== fieldName) {
              acc[key] = state.errors[key];
            }
            return acc;
          }, {} as Record<string, string>)
        }));
      },

      // Navigation
      goToStep: (stepId: string) => {
        const currentState = get();
        set({
          stepHistory: [...currentState.stepHistory, currentState.currentStepId],
          currentStepId: stepId,
          visitedSteps: currentState.visitedSteps.includes(stepId)
            ? currentState.visitedSteps
            : [...currentState.visitedSteps, stepId]
        });
      },

      goToNextStep: () => {
        const currentState = get();
        const hasCoBorrower = currentState.formData.hasCoBorrower === true;

        // Définir la séquence complète des étapes
        let steps = [
          'welcome',
          'simulation',
          'duration',
          'personal_info'
        ];

        // Ajouter co_borrower juste après personal_info si nécessaire
        if (hasCoBorrower) {
          steps.push('co_borrower');
        }

        steps.push(
          'personal_situation',
          'address',
          'insurance',
          'summary'
        );

        const currentIndex = steps.indexOf(currentState.currentStepId);
        if (currentIndex < steps.length - 1) {
          // Marquer l'étape courante comme complétée avant d'avancer
          currentState.completeStep(currentState.currentStepId);
          currentState.goToStep(steps[currentIndex + 1]);
        }
      },

      goToPreviousStep: () => {
        const currentState = get();
        if (currentState.stepHistory.length > 0) {
          const previousStepId = currentState.stepHistory[currentState.stepHistory.length - 1];
          set({
            currentStepId: previousStepId,
            stepHistory: currentState.stepHistory.slice(0, -1)
          });
        }
      },

      completeStep: (stepId: string) => {
        set(state => ({
          completedSteps: state.completedSteps.includes(stepId) 
            ? state.completedSteps 
            : [...state.completedSteps, stepId]
        }));
      },

      // Gestion des erreurs
      setErrors: (errors: Record<string, string>) => {
        set({ errors });
      },

      clearErrors: () => {
        set({ errors: {} });
      },

      // Soumission du formulaire
      submitForm: async () => {
        const state = get();
        set({ isSubmitting: true, errors: {} });

        try {
          const response = await submitCreditApplication(state.formData);

          if (response.success) {
            // Stocker la réponse de l'API
            console.log('Formulaire soumis avec succès:', response.data);
            set({ apiResponse: response.data });

            // La conversion Affilae est envoyée côté serveur via postback S2S

            // Naviguer vers l'écran de succès
            get().goToStep('success');
          } else {
            throw new Error(response.error || 'Erreur lors de la soumission');
          }
        } catch (error) {
          console.error('Erreur lors de la soumission:', error);
          set({
            errors: {
              submit: error instanceof Error ? error.message : 'Erreur inconnue'
            }
          });
        } finally {
          set({ isSubmitting: false });
        }
      },

      // Sauvegarde brouillon
      saveDraft: async () => {
        const state = get();
        
        try {
          set({ isLoading: true });
          
          await saveDraftApplication({
            formData: state.formData,
            currentStepId: state.currentStepId,
            timestamp: new Date().toISOString()
          });
          
          console.log('Brouillon sauvegardé');
        } catch (error) {
          console.error('Erreur lors de la sauvegarde du brouillon:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      // Réinitialisation
      resetForm: () => {
        set({
          formData: {},
          currentStepId: 'welcome',
          completedSteps: [],
          stepHistory: [],
          visitedSteps: ['welcome'],
          isSubmitting: false,
          isLoading: false,
          errors: {},
          apiResponse: undefined
        });
      }
    }),
    {
      name: 'yelloan-credit-form',
      storage: createJSONStorage(() => localStorage),
      // Ne sauvegarder que certains champs sensibles
      partialize: (state) => ({
        formData: state.formData,
        currentStepId: state.currentStepId,
        completedSteps: state.completedSteps,
        stepHistory: state.stepHistory,
        visitedSteps: state.visitedSteps
      })
    }
  )
);

// Hook pour accéder aux données du formulaire par étape
export const useStepData = (stepId: string) => {
  return useFormStore(state => {
    const stepData: Record<string, any> = {};
    
    // Filtrer les données selon l'étape
    Object.entries(state.formData).forEach(([key, value]) => {
      if (key.startsWith(stepId) || key.includes(stepId)) {
        stepData[key] = value;
      }
    });
    
    return stepData;
  });
};

// Hook pour vérifier si une étape est complète
export const useIsStepComplete = (stepId: string) => {
  return useFormStore(state => state.completedSteps.includes(stepId));
};

// Hook pour la navigation
export const useNavigation = () => {
  return useFormStore(state => ({
    currentStepId: state.currentStepId,
    goToStep: state.goToStep,
    goToNextStep: state.goToNextStep,
    goToPreviousStep: state.goToPreviousStep,
    canGoBack: state.stepHistory.length > 0
  }));
};